/**
 * MaintenanceAdmin — admin CRUD interface for maintenance windows.
 *
 * Route: /admin/status/maintenance (requires admin role; returns 403 for non-admin)
 *
 * Features:
 *   - Table with columns: Title, Start, End, Status, Affected Components (count), Actions
 *   - Filter dropdown: All, Scheduled, Active, Completed, Cancelled
 *   - Create form with validation (start_at < end_at before submit)
 *   - Edit form: disables start_at/end_at/affected_components after window started
 *   - Cancel with confirmation dialog "Are you sure?"
 *   - Success green toast on create/update/cancel
 *   - Error red toast with API error message on failure
 *   - Form states: validation errors (red border), submit loading (spinner), submit disabled (gray)
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { statusApi, type MaintenanceWindow, StatusApiError } from '../api/status.js';
import { useToast } from '../contexts/ToastContext.js';

// ── Types ─────────────────────────────────────────────────────────────────────

type FilterStatus = 'all' | 'scheduled' | 'active' | 'completed' | 'cancelled';

interface FormData {
  title: string;
  message: string;
  start_at: string;
  end_at: string;
  affected_components: string;
}

const EMPTY_FORM: FormData = {
  title: '',
  message: '',
  start_at: '',
  end_at: '',
  affected_components: '',
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatDateTime(isoString: string): string {
  return new Date(isoString).toLocaleString(undefined, {
    dateStyle: 'short',
    timeStyle: 'short',
  });
}

function toDatetimeLocal(isoString: string): string {
  const d = new Date(isoString);
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function getStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    scheduled: 'Scheduled',
    active: 'Active',
    in_progress: 'Active',
    completed: 'Completed',
    cancelled: 'Cancelled',
  };
  return labels[status] ?? status;
}

function getStatusBadgeClass(status: string): string {
  const classes: Record<string, string> = {
    scheduled: 'bg-blue-100 text-blue-700',
    active: 'bg-amber-100 text-amber-700',
    in_progress: 'bg-amber-100 text-amber-700',
    completed: 'bg-emerald-100 text-emerald-700',
    cancelled: 'bg-neutral-100 text-neutral-500',
  };
  return classes[status] ?? 'bg-neutral-100 text-neutral-600';
}

function windowHasStarted(win: MaintenanceWindow): boolean {
  const startAt = new Date(win.start_at ?? win.scheduled_start ?? '');
  return startAt <= new Date();
}

// ── Create/Edit Form ──────────────────────────────────────────────────────────

interface WindowFormProps {
  initialData?: MaintenanceWindow;
  onSuccess: (win: MaintenanceWindow) => void;
  onCancel: () => void;
  adminToken: string;
}

function WindowForm({ initialData, onSuccess, onCancel, adminToken }: WindowFormProps) {
  const { success: toastSuccess, error: toastError } = useToast();
  const isEdit = !!initialData;
  const started = isEdit ? windowHasStarted(initialData) : false;

  const [form, setForm] = useState<FormData>(() => {
    if (initialData) {
      return {
        title: initialData.title,
        message: initialData.message ?? '',
        start_at: toDatetimeLocal(initialData.start_at ?? initialData.scheduled_start ?? ''),
        end_at: toDatetimeLocal(initialData.end_at ?? initialData.scheduled_end ?? ''),
        affected_components: (initialData.affected_components ?? []).join(', '),
      };
    }
    return { ...EMPTY_FORM };
  });

  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});
  const [submitting, setSubmitting] = useState(false);

  function validate(): boolean {
    const newErrors: Partial<Record<keyof FormData, string>> = {};

    if (!form.title.trim()) newErrors.title = 'Title is required';
    if (!form.start_at) newErrors.start_at = 'Start time is required';
    if (!form.end_at) newErrors.end_at = 'End time is required';

    if (form.start_at && form.end_at) {
      if (new Date(form.start_at) >= new Date(form.end_at)) {
        newErrors.start_at = 'Start must be before end';
        newErrors.end_at = 'End must be after start';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    try {
      const affected = form.affected_components
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);

      let result: { maintenance_window: MaintenanceWindow };

      if (isEdit && initialData) {
        const patch: Parameters<typeof statusApi.updateMaintenanceWindow>[1] = {
          title: form.title,
          message: form.message,
          updated_at: initialData.updated_at,
        };
        if (!started) {
          patch.start_at = new Date(form.start_at).toISOString();
          patch.end_at = new Date(form.end_at).toISOString();
          patch.affected_components = affected;
        }
        result = await statusApi.updateMaintenanceWindow(initialData.id, patch, adminToken);
        toastSuccess('Maintenance window updated successfully');
      } else {
        result = await statusApi.createMaintenanceWindow(
          {
            title: form.title,
            message: form.message,
            start_at: new Date(form.start_at).toISOString(),
            end_at: new Date(form.end_at).toISOString(),
            affected_components: affected,
          },
          adminToken,
        );
        toastSuccess('Maintenance window created successfully');
      }

      onSuccess(result.maintenance_window);
    } catch (err) {
      const msg = err instanceof StatusApiError ? err.message : 'An error occurred. Please try again.';
      toastError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  const inputClass = (field: keyof FormData) =>
    `w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400 ${
      errors[field] ? 'border-red-400 bg-red-50' : 'border-neutral-300 bg-white'
    }`;

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-4">
      <h2 className="text-lg font-semibold text-neutral-900">
        {isEdit ? 'Edit Maintenance Window' : 'New Maintenance Window'}
      </h2>

      {/* Title */}
      <div>
        <label htmlFor="mw-title" className="block text-sm font-medium text-neutral-700 mb-1">
          Title <span className="text-red-500">*</span>
        </label>
        <input
          id="mw-title"
          type="text"
          value={form.title}
          onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
          className={inputClass('title')}
          maxLength={200}
          disabled={submitting}
          aria-describedby={errors.title ? 'mw-title-error' : undefined}
        />
        {errors.title && (
          <p id="mw-title-error" className="text-xs text-red-600 mt-1">{errors.title}</p>
        )}
      </div>

      {/* Start / End */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="mw-start" className="block text-sm font-medium text-neutral-700 mb-1">
            Start Time <span className="text-red-500">*</span>
          </label>
          <input
            id="mw-start"
            type="datetime-local"
            value={form.start_at}
            onChange={(e) => setForm((f) => ({ ...f, start_at: e.target.value }))}
            className={inputClass('start_at')}
            disabled={submitting || (isEdit && started)}
            aria-describedby={errors.start_at ? 'mw-start-error' : undefined}
          />
          {errors.start_at && (
            <p id="mw-start-error" className="text-xs text-red-600 mt-1">{errors.start_at}</p>
          )}
          {isEdit && started && (
            <p className="text-xs text-neutral-400 mt-1">Cannot change after window started</p>
          )}
        </div>
        <div>
          <label htmlFor="mw-end" className="block text-sm font-medium text-neutral-700 mb-1">
            End Time <span className="text-red-500">*</span>
          </label>
          <input
            id="mw-end"
            type="datetime-local"
            value={form.end_at}
            onChange={(e) => setForm((f) => ({ ...f, end_at: e.target.value }))}
            className={inputClass('end_at')}
            disabled={submitting || (isEdit && started)}
            aria-describedby={errors.end_at ? 'mw-end-error' : undefined}
          />
          {errors.end_at && (
            <p id="mw-end-error" className="text-xs text-red-600 mt-1">{errors.end_at}</p>
          )}
        </div>
      </div>

      {/* Affected Components */}
      <div>
        <label htmlFor="mw-components" className="block text-sm font-medium text-neutral-700 mb-1">
          Affected Component IDs <span className="text-neutral-400 font-normal">(comma-separated)</span>
        </label>
        <input
          id="mw-components"
          type="text"
          value={form.affected_components}
          onChange={(e) => setForm((f) => ({ ...f, affected_components: e.target.value }))}
          className={inputClass('affected_components')}
          placeholder="e.g. api-component-id, dashboard-id"
          disabled={submitting || (isEdit && started)}
          aria-describedby={errors.affected_components ? 'mw-comp-error' : undefined}
        />
        {errors.affected_components && (
          <p id="mw-comp-error" className="text-xs text-red-600 mt-1">{errors.affected_components}</p>
        )}
        {isEdit && started && (
          <p className="text-xs text-neutral-400 mt-1">Cannot change after window started</p>
        )}
      </div>

      {/* Message */}
      <div>
        <label htmlFor="mw-message" className="block text-sm font-medium text-neutral-700 mb-1">
          Message
        </label>
        <textarea
          id="mw-message"
          value={form.message}
          onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
          rows={3}
          className={`${inputClass('message')} resize-y`}
          maxLength={5000}
          disabled={submitting}
        />
      </div>

      {/* Actions */}
      <div className="flex items-center gap-3 pt-2">
        <button
          type="submit"
          disabled={submitting}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors focus:outline-none focus:ring-1 focus:ring-blue-400 ${
            submitting
              ? 'bg-neutral-300 text-neutral-500 cursor-not-allowed'
              : 'bg-blue-600 text-white hover:bg-blue-700'
          }`}
          aria-busy={submitting}
        >
          {submitting ? (
            <span className="flex items-center gap-2">
              <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Saving...
            </span>
          ) : isEdit ? 'Save Changes' : 'Create Window'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          className="px-4 py-2 rounded-md text-sm font-medium text-neutral-700 hover:bg-neutral-100 transition-colors focus:outline-none focus:ring-1 focus:ring-neutral-300"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function MaintenanceAdminPage() {
  const { success: toastSuccess, error: toastError } = useToast();
  const [windows, setWindows] = useState<MaintenanceWindow[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<FilterStatus>('all');
  const [editingWindow, setEditingWindow] = useState<MaintenanceWindow | null | 'new'>(null);
  const [cancelTarget, setCancelTarget] = useState<MaintenanceWindow | null>(null);
  const [cancelSubmitting, setCancelSubmitting] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const mountedRef = useRef(true);

  // In a real app this would come from auth context; here we read from localStorage
  const adminToken = localStorage.getItem('admin_token') ?? '';

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const fetchWindows = useCallback(async () => {
    setLoading(true);
    try {
      const statusParam = filterStatus === 'all'
        ? 'scheduled,active,completed,cancelled'
        : filterStatus;
      const result = await statusApi.getMaintenanceWindows({
        status: statusParam,
        limit: 100,
      });
      if (mountedRef.current) setWindows(result.maintenance_windows);
    } catch (err) {
      if (err instanceof StatusApiError && err.status === 403) {
        setAccessDenied(true);
      } else {
        toastError('Failed to load maintenance windows');
      }
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [filterStatus, toastError]);

  useEffect(() => {
    fetchWindows();
  }, [fetchWindows]);

  // Access denied state
  if (accessDenied) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-lg font-medium text-neutral-700 mb-2">Access Denied</p>
          <p className="text-sm text-neutral-500">You need admin privileges to manage maintenance windows.</p>
        </div>
      </div>
    );
  }

  // Handle cancel confirmation
  async function handleConfirmCancel() {
    if (!cancelTarget) return;
    setCancelSubmitting(true);
    try {
      const result = await statusApi.updateMaintenanceWindow(
        cancelTarget.id,
        { status: 'cancelled', updated_at: cancelTarget.updated_at },
        adminToken,
      );
      setWindows((prev) =>
        prev.map((w) => w.id === cancelTarget.id ? result.maintenance_window : w),
      );
      toastSuccess('Maintenance window cancelled');
      setCancelTarget(null);
    } catch (err) {
      const msg = err instanceof StatusApiError ? err.message : 'Failed to cancel window';
      toastError(msg);
    } finally {
      setCancelSubmitting(false);
    }
  }

  // Filter windows for display
  const filteredWindows = windows.filter((w) => {
    if (filterStatus === 'all') return true;
    if (filterStatus === 'active') return w.status === 'active' || w.status === 'in_progress';
    return w.status === filterStatus;
  });

  return (
    <div className="px-4 py-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <h1 className="text-2xl font-semibold text-neutral-900">Maintenance Windows</h1>
        <button
          type="button"
          onClick={() => setEditingWindow('new')}
          className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-1 focus:ring-blue-400 transition-colors"
        >
          + New Window
        </button>
      </div>

      {/* Create/Edit Form */}
      {editingWindow !== null && (
        <div className="mb-6 border border-neutral-200 rounded-xl p-6 bg-white shadow-sm">
          <WindowForm
            initialData={editingWindow === 'new' ? undefined : editingWindow}
            adminToken={adminToken}
            onSuccess={(win) => {
              if (editingWindow === 'new') {
                setWindows((prev) => [win, ...prev]);
              } else {
                setWindows((prev) => prev.map((w) => w.id === win.id ? win : w));
              }
              setEditingWindow(null);
            }}
            onCancel={() => setEditingWindow(null)}
          />
        </div>
      )}

      {/* Filter */}
      <div className="flex items-center gap-3 mb-4">
        <label htmlFor="status-filter" className="text-sm text-neutral-600">Filter:</label>
        <select
          id="status-filter"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value as FilterStatus)}
          className="text-sm border border-neutral-300 rounded-md px-2 py-1.5 bg-white text-neutral-800 focus:outline-none focus:ring-1 focus:ring-blue-400"
          aria-label="Filter maintenance windows by status"
        >
          <option value="all">All</option>
          <option value="scheduled">Scheduled</option>
          <option value="active">Active</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      {/* Table */}
      {loading ? (
        <div className="animate-pulse space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-14 bg-neutral-100 rounded-lg" />
          ))}
        </div>
      ) : filteredWindows.length === 0 ? (
        <div className="text-center py-12 text-neutral-500">
          No maintenance windows found.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-neutral-200">
          <table className="w-full text-sm text-left">
            <thead>
              <tr className="bg-neutral-50 border-b border-neutral-200">
                <th className="px-4 py-3 font-medium text-neutral-600">Title</th>
                <th className="px-4 py-3 font-medium text-neutral-600">Start</th>
                <th className="px-4 py-3 font-medium text-neutral-600">End</th>
                <th className="px-4 py-3 font-medium text-neutral-600">Status</th>
                <th className="px-4 py-3 font-medium text-neutral-600">Components</th>
                <th className="px-4 py-3 font-medium text-neutral-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {filteredWindows.map((win) => {
                const startAt = win.start_at ?? win.scheduled_start ?? '';
                const endAt = win.end_at ?? win.scheduled_end ?? '';
                const isCancellable = win.status !== 'cancelled' && win.status !== 'completed';
                const isEditable = win.status !== 'cancelled' && win.status !== 'completed';

                return (
                  <tr key={win.id} className="hover:bg-neutral-50 transition-colors">
                    <td className="px-4 py-3 font-medium text-neutral-900 max-w-xs truncate">
                      {win.title}
                    </td>
                    <td className="px-4 py-3 text-neutral-600 whitespace-nowrap">
                      {formatDateTime(startAt)}
                    </td>
                    <td className="px-4 py-3 text-neutral-600 whitespace-nowrap">
                      {formatDateTime(endAt)}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClass(win.status)}`}>
                        {getStatusLabel(win.status)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-neutral-600">
                      {win.affected_components.length}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {isEditable && (
                          <button
                            type="button"
                            onClick={() => setEditingWindow(win)}
                            className="text-blue-600 hover:text-blue-800 text-xs font-medium focus:outline-none focus:underline"
                            aria-label={`Edit ${win.title}`}
                          >
                            Edit
                          </button>
                        )}
                        {isCancellable && (
                          <button
                            type="button"
                            onClick={() => setCancelTarget(win)}
                            className="text-red-600 hover:text-red-800 text-xs font-medium focus:outline-none focus:underline"
                            aria-label={`Cancel ${win.title}`}
                          >
                            Cancel
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Confirmation dialog */}
      {cancelTarget && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          role="dialog"
          aria-modal="true"
          aria-label="Confirm cancellation"
        >
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-sm w-full mx-4">
            <h2 className="text-lg font-semibold text-neutral-900 mb-2">Cancel Maintenance?</h2>
            <p className="text-sm text-neutral-600 mb-1">Are you sure?</p>
            <p className="text-sm text-neutral-500 mb-6">
              This will cancel <strong>{cancelTarget.title}</strong> and notify subscribers.
            </p>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={handleConfirmCancel}
                disabled={cancelSubmitting}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors focus:outline-none focus:ring-1 focus:ring-red-400 ${
                  cancelSubmitting
                    ? 'bg-neutral-200 text-neutral-400 cursor-not-allowed'
                    : 'bg-red-600 text-white hover:bg-red-700'
                }`}
                aria-busy={cancelSubmitting}
              >
                {cancelSubmitting ? 'Cancelling...' : 'Yes, Cancel'}
              </button>
              <button
                type="button"
                onClick={() => setCancelTarget(null)}
                disabled={cancelSubmitting}
                className="px-4 py-2 rounded-md text-sm font-medium text-neutral-700 hover:bg-neutral-100 transition-colors focus:outline-none focus:ring-1 focus:ring-neutral-300"
              >
                No, Keep It
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
